import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { judgingService } from '../services/judgingService';
import { useAuth } from '../contexts/AuthContext';

const JudgeEventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStory, setSelectedStory] = useState(null);
  const [judgingDialogOpen, setJudgingDialogOpen] = useState(false);
  const [judgingComment, setJudgingComment] = useState('');
  const [judgingScore, setJudgingScore] = useState(0);
  const [isFinal, setIsFinal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEventStories();
  }, [eventId]);

  const fetchEventStories = async () => {
    try {
      setLoading(true);
      const response = await judgingService.getEventStoriesForJudging(eventId);
      setEvent(response.data.event);
      setStories(response.data.stories || []);
    } catch (err) {
      console.error('Error fetching event stories:', err);
      setError('Failed to load event stories');
    } finally {
      setLoading(false);
    }
  };

  const handleJudgeStory = (story) => {
    setSelectedStory(story);
    // Pre-fill form if there's an existing judging comment
    if (story.judging_comment) {
      setJudgingComment(story.judging_comment.content || '');
      setJudgingScore(story.judging_comment.score || 0);
      setIsFinal(story.judging_comment.is_final || false);
    } else {
      setJudgingComment('');
      setJudgingScore(0);
      setIsFinal(false);
    }
    setJudgingDialogOpen(true);
  };

  const handleSubmitJudgment = async () => {
    if (!judgingComment.trim()) {
      setError('Please provide a judging comment');
      return;
    }

    try {
      setSubmitting(true);
      const judgmentData = {
        content: judgingComment.trim(),
        score: judgingScore > 0 ? judgingScore : null,
        is_final: isFinal
      };

      await judgingService.submitJudgingComment(eventId, selectedStory.story_id, judgmentData);
      
      // Refresh the stories list to show updated judging status
      await fetchEventStories();
      
      setJudgingDialogOpen(false);
      setSelectedStory(null);
      setError('');
    } catch (err) {
      console.error('Error submitting judgment:', err);
      setError('Failed to submit judgment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setJudgingDialogOpen(false);
    setSelectedStory(null);
    setJudgingComment('');
    setJudgingScore(0);
    setIsFinal(false);
  };

  const getJudgingStatus = (story) => {
    // Check if current judge has judged this story
    const hasMyJudgment = !!story.judging_comment;
    
    // Check if any judge has judged this story
    const hasAnyJudgment = story.has_any_judging_comments;
    const totalJudgments = story.total_judging_comments || 0;
    
    if (hasMyJudgment) {
      if (story.judging_comment.is_final) {
        return { label: 'My Final Judgment', color: 'success' };
      }
      return { label: 'My Draft', color: 'warning' };
    } else if (hasAnyJudgment) {
      return { 
        label: `Judged by Others (${totalJudgments})`, 
        color: 'info' 
      };
    }
    
    return { label: 'Not Judged', color: 'default' };
  };

  if (!user?.role || !['judge', 'admin'].includes(user.role)) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page. Only judges and admins can judge events.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading event stories...
        </Typography>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Event not found or you don't have permission to judge this event.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button onClick={() => navigate('/judge/events')} sx={{ mb: 2 }}>
          ← Back to Events
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Judge Event: {event.title}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          {event.description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${stories.length} Stories`} />
          <Chip 
            label={`${stories.filter(s => s.judging_comment?.is_final).length} My Final Judgments`} 
            color="success" 
          />
          <Chip 
            label={`${stories.filter(s => s.judging_comment && !s.judging_comment.is_final).length} My Drafts`} 
            color="warning" 
          />
          <Chip 
            label={`${stories.filter(s => s.has_any_judging_comments).length} Stories Judged by Anyone`} 
            color="info" 
          />
          <Chip 
            label={`${stories.filter(s => !s.has_any_judging_comments).length} Unjudged Stories`} 
            color="default" 
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {stories.length === 0 ? (
        <Alert severity="info">
          No stories have been submitted to this event yet.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {stories.map((story) => {
            const judgingStatus = getJudgingStatus(story);
            return (
              <Grid item xs={12} md={6} key={story.story_id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {story.title}
                      </Typography>
                      <Chip
                        label={judgingStatus.label}
                        color={judgingStatus.color}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {story.content.substring(0, 200)}...
                    </Typography>
                    
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      <strong>Author:</strong> {story.author_name || 'Anonymous'}
                    </Typography>
                    
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      <strong>Word Count:</strong> {story.word_count || 'N/A'}
                    </Typography>

                    {/* Show if other judges have judged this story */}
                    {!story.judging_comment && story.has_any_judging_comments && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                        <Typography variant="subtitle2" gutterBottom color="info.main">
                          📋 Already Judged by Others
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This story has {story.total_judging_comments} judgment{story.total_judging_comments !== 1 ? 's' : ''} from other judges.
                        </Typography>
                      </Box>
                    )}

                    {story.judging_comment && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Your Judgment:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {story.judging_comment.content.substring(0, 100)}...
                        </Typography>
                        {story.judging_comment.score && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption">Score:</Typography>
                            <Rating value={story.judging_comment.score} readOnly size="small" />
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                  
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant={story.judging_comment ? 'outlined' : 'contained'}
                      fullWidth
                      onClick={() => handleJudgeStory(story)}
                    >
                      {story.judging_comment ? 'Edit Judgment' : 'Judge Story'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Judging Dialog */}
      <Dialog 
        open={judgingDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Judge Story: {selectedStory?.title}
        </DialogTitle>
        <DialogContent>
          {selectedStory && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Story Content:
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedStory.content}
                </Typography>
              </Box>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>
            Your Judgment:
          </Typography>
          
          <TextField
            label="Judging Comments"
            multiline
            rows={6}
            fullWidth
            value={judgingComment}
            onChange={(e) => setJudgingComment(e.target.value)}
            placeholder="Provide detailed feedback on the story's strengths, weaknesses, and overall quality..."
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ mb: 3 }}>
            <Typography component="legend" gutterBottom>
              Score (Optional):
            </Typography>
            <Rating
              value={judgingScore}
              onChange={(event, newValue) => setJudgingScore(newValue || 0)}
              max={5}
              size="large"
            />
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isFinal}
                onChange={(e) => setIsFinal(e.target.checked)}
              />
            }
            label="Mark as final judgment (cannot be easily changed later)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitJudgment}
            variant="contained"
            disabled={submitting || !judgingComment.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit Judgment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JudgeEventDetail;
